import React, { useState, useEffect, useCallback } from 'react';
import { dataService, DataFile } from '../services/dataService';
import { fileLabelService, FileLabel } from '../services/fileLabelService';
import { FileData, MarkedRange, ImageAnnotation } from '../types/training';
import EnhancedFilePreview from '../components/training/EnhancedFilePreview';
import { isTextFile, isImageFile } from '../utils/filePreviewUtils';
import './DataLabelingPage.css';

interface DataGroupFile {
  name: string;
  path: string;
  signed_url: string;
}

const DataLabelingPage: React.FC = () => {
  const [dataGroups, setDataGroups] = useState<DataFile[]>([]);
  const [selectedDataGroup, setSelectedDataGroup] = useState<DataFile | null>(null);
  const [groupFiles, setGroupFiles] = useState<DataGroupFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<DataGroupFile | null>(null);
  const [previewFile, setPreviewFile] = useState<FileData | null>(null);
  const [fileLabel, setFileLabel] = useState<FileLabel | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileSearchQuery, setFileSearchQuery] = useState('');

  // Load data groups on mount
  useEffect(() => {
    loadDataGroups();
  }, []);

  // Load files when data group is selected
  useEffect(() => {
    if (selectedDataGroup) {
      loadGroupFiles(selectedDataGroup.id);
    } else {
      setGroupFiles([]);
      setSelectedFile(null);
      setPreviewFile(null);
    }
  }, [selectedDataGroup]);

  // Load file label when file is selected
  useEffect(() => {
    if (selectedFile && selectedDataGroup) {
      loadFileLabel(selectedDataGroup.id, selectedFile.name);
    }
  }, [selectedFile, selectedDataGroup]);

  const loadDataGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const files = await dataService.listData();
      console.log('[DataLabelingPage] Loaded data:', files);
      setDataGroups(files);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load data';
      setError(errorMsg);
      console.error('[DataLabelingPage] Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupFiles = async (dataGroupId: string) => {
    setLoadingFiles(true);
    try {
      const files = await dataService.listDataFiles(dataGroupId);
      console.log('[DataLabelingPage] Loaded group files:', files);
      // Filter out any folders that might have slipped through
      const actualFiles = files.filter(f => f.name && f.name.includes('.'));
      setGroupFiles(actualFiles);
    } catch (err: any) {
      console.error('[DataLabelingPage] Error loading group files:', err);
      setGroupFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadFileLabel = async (dataFileId: string, fileName: string) => {
    try {
      const labels = await fileLabelService.listFileLabels({ data_file_id: dataFileId });
      // Find label that matches this specific file by checking metadata
      const label = labels.find(l => 
        l.metadata?.file_name === fileName || 
        l.metadata?.file_path?.endsWith(fileName)
      ) || null;
      setFileLabel(label);
    } catch (err: any) {
      console.error('[DataLabelingPage] Error loading file label:', err);
      setFileLabel(null);
    }
  };

  const handleFileClick = async (file: DataGroupFile) => {
    setSelectedFile(file);
    setLoadingPreview(true);

    try {
      // Fetch file content
      let fileData: FileData;
      
      if (isImageFile(file.name)) {
        // For images, use the signed URL directly
        fileData = {
          id: file.path,
          name: file.name,
          type: 'image',
          textContent: file.signed_url,
          size: 0,
          marks: fileLabel?.marks || [],
          annotations: fileLabel?.annotations || [],
        };
      } else if (isTextFile(file.name)) {
        // For text files, fetch content
        const response = await fetch(file.signed_url);
        const textContent = await response.text();
        fileData = {
          id: file.path,
          name: file.name,
          type: 'text',
          textContent: textContent,
          size: textContent.length,
          marks: fileLabel?.marks || [],
          annotations: fileLabel?.annotations || [],
        };
      } else {
        // For other files, just show name
        fileData = {
          id: file.path,
          name: file.name,
          type: 'unknown',
          size: 0,
          marks: fileLabel?.marks || [],
          annotations: fileLabel?.annotations || [],
        };
      }

      setPreviewFile(fileData);
    } catch (err: any) {
      console.error('[DataLabelingPage] Error loading file:', err);
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFileUpdate = useCallback(async (updatedFile: FileData) => {
    if (!selectedFile || !selectedDataGroup) return;

    try {
      // Save labels to backend
      const savedLabel = await fileLabelService.saveFileLabel({
        data_file_id: selectedDataGroup.id,
        annotations: updatedFile.annotations || [],
        marks: updatedFile.marks || [],
        labels: [],
        metadata: {
          file_name: selectedFile.name,
          file_path: selectedFile.path,
        },
        status: 'labeled',
      });

      // Update local state
      setPreviewFile(updatedFile);
      setFileLabel(savedLabel);
    } catch (err: any) {
      console.error('[DataLabelingPage] Error saving file label:', err);
      setError(`Failed to save labels: ${err.message}`);
    }
  }, [selectedFile, selectedDataGroup]);

  const filteredDataGroups = dataGroups.filter((group) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return group.name.toLowerCase().includes(query);
  });

  const filteredGroupFiles = groupFiles.filter((file) => {
    if (!fileSearchQuery) return true;
    const query = fileSearchQuery.toLowerCase();
    return file.name.toLowerCase().includes(query);
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getFileIcon = (fileName: string) => {
    if (isImageFile(fileName)) return '🖼️';
    if (isTextFile(fileName)) return '📄';
    return '📁';
  };

  return (
    <div className="data-labeling-page">
      <div className="data-labeling-header">
        <h1>Data Labeling</h1>
        <p>Label and annotate your data files</p>
      </div>

      {/* Top Section - Data Selector (Horizontal) */}
      <div className="data-selector-section">
        <div className="data-selector-header">
          <h2>Data</h2>
          <input
            type="text"
            placeholder="Search data..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="data-selector-content">
          {loading ? (
            <div className="loading-state">Loading data...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : filteredDataGroups.length > 0 ? (
            <div className="data-list-horizontal">
              {filteredDataGroups.map((group) => (
                <div
                  key={group.id}
                  className={`data-card ${selectedDataGroup?.id === group.id ? 'selected' : ''}`}
                  onClick={() => setSelectedDataGroup(group)}
                >
                  <div className="data-card-name">{group.name}</div>
                  <div className="data-card-meta">
                    <span className="data-card-type">{group.data_type}</span>
                    <span className="data-card-date">{formatDate(group.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">No data found</div>
          )}
        </div>
      </div>

      {/* Bottom Section - Files and Preview (2 columns) */}
      <div className="data-labeling-layout">
        {/* Left Column - File List Sidebar */}
        <div className="file-list-section">
          {selectedDataGroup ? (
            <>
              <div className="section-header">
                <h2>Files</h2>
                <input
                  type="text"
                  placeholder="Search files..."
                  className="search-input"
                  value={fileSearchQuery}
                  onChange={(e) => setFileSearchQuery(e.target.value)}
                />
              </div>
              <div className="section-content">
                {loadingFiles ? (
                  <div className="loading-state">Loading files...</div>
                ) : filteredGroupFiles.length > 0 ? (
                  <div className="file-list">
                    {filteredGroupFiles.map((file) => {
                      const hasLabel = fileLabel && selectedFile?.name === file.name;
                      return (
                        <div
                          key={file.path}
                          className={`file-list-item ${selectedFile?.path === file.path ? 'selected' : ''} ${hasLabel ? 'labeled' : ''}`}
                          onClick={() => handleFileClick(file)}
                        >
                          <span className="file-icon">{getFileIcon(file.name)}</span>
                          <span className="file-name">{file.name}</span>
                          {hasLabel && <span className="label-badge">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state">No files found</div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-section">
              <p>Select data to view files</p>
            </div>
          )}
        </div>

        {/* Right Column - File Preview */}
        <div className="file-preview-section">
          {previewFile ? (
            <div className="preview-container">
              {loadingPreview ? (
                <div className="loading-state">Loading file...</div>
              ) : (
                <EnhancedFilePreview
                  file={previewFile}
                  onFileUpdate={handleFileUpdate}
                  onClose={() => {
                    setPreviewFile(null);
                    setSelectedFile(null);
                  }}
                  isOpen={!!previewFile}
                  embedded={true}
                />
              )}
            </div>
          ) : (
            <div className="empty-section">
              <p>Select a file to preview and label</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataLabelingPage;
